/* @flow */
import * as React from 'react';
import { graphql } from 'react-apollo';
import cx from 'classnames';
import idx from 'idx';

import CurrentUser from 'auth/CurrentUser';
import FormElement from 'components/FormElement';
import Glyph from 'components/Glyph';
import Group from 'entities/Group';
import LayerTrigger from 'components/LayerTrigger';
import Link from 'components/Link';
import Menu from 'components/Menu';
import MenuItem from 'components/MenuItem';
import Select from 'components/Select';

import GROUP_SCHOOL_SELECT_QUERY from 'groups/queries/GroupSchoolSelectQuery.gql';
import type { GroupSchoolSelect as GroupSchoolSelectQueryType } from 'groups/queries/__generated__/GroupSchoolSelect';

import './styles/GroupSchoolSelect.scss';

export const UNAFFILIATED_CLASS_ID = '-1';
export const unaffiliatedOrgOption = {
  value: UNAFFILIATED_CLASS_ID,
  label: 'Not affiliated with a school',
};
export type OrgOption = { value: string, label: string };

type Props = {
  onSchoolChange?: ?(id: ?string, uuid?: ?string) => void,
  group?: ?Group,
  hideUnaffiliated?: ?boolean,
  filterUnadministered?: ?boolean,
  prefix?: ?string,
  input?: ?string,
  data: ReactApolloData<GroupSchoolSelectQueryType>,
  initialOrganization: OrgOption,
};

type State = {
  selectedOrg: OrgOption,
};

class GroupSchoolSelect extends React.Component<Props, State> {
  static defaultProps = {
    input: 'select',
    hideUnaffiliated: false,
    filterUnadministered: false,
  };

  constructor(props: Props) {
    super(props);
    const orgOptions = this.getSchoolSelectOptions();

    this.state = {
      selectedOrg: props.initialOrganization || orgOptions[0] || unaffiliatedOrgOption,
    };
  }

  componentDidMount() {
    this.handleSchoolChange(this.state.selectedOrg.value);
  }

  componentWillReceiveProps(nextProps: Props) {
    if (this.props.hideUnaffiliated !== nextProps.hideUnaffiliated) {
      this.handleSchoolChange(this.getSelectedSchoolId());
    }
  }

  componentDidUpdate(prevProps: Props) {
    const prevOrganizations = idx(prevProps.data, _ => _.me.organizations);
    const organizations = idx(this.props.data, _ => _.me.organizations);
    if (!prevOrganizations && organizations) {
      const orgOptions = this.getSchoolSelectOptions();
      if (orgOptions.length > 0) {
        this.handleSchoolChange(orgOptions[0].value);
      }
    }
  }

  unaffiliatedIsInvalid(): boolean {
    return this.props.hideUnaffiliated || (!!this.props.group && this.props.group.hasChildren());
  }

  handleSchoolChange = (orgIdString: string) => {
    const orgOptions = this.getSchoolSelectOptions();
    const organizations = idx(this.props.data, _ => _.me.organizations);
    const selectedOrgOption = orgOptions.find(o => o.value === orgIdString);
    this.setState({
      selectedOrg: selectedOrgOption,
    });
    const { onSchoolChange } = this.props;
    if (onSchoolChange) {
      const orgId = orgIdString; // this can be UNAFFILIATED_CLASS_ID (-1)
      const matchingOrg = organizations && organizations.find(org => org.id.toString() === orgId);
      const orgUuid = matchingOrg && matchingOrg.uuid;
      onSchoolChange(orgId, orgUuid);
    }
  };

  getSchoolSelectOptions = () => {
    const organizations = idx(this.props.data, _ => _.me.organizations);
    if (!organizations) {
      return [];
    }

    const validOrgs = organizations.reduce((arr, o) => {
      if (
        o.type !== 'District' &&
        (!this.props.filterUnadministered || (o.admins && o.admins.length > 0))
      ) {
        arr.push(o);
      }
      return arr;
    }, []);

    const organizationOptions = validOrgs.map<OrgOption>(org => {
      return { value: org.id.toString(), label: org.name };
    });

    if (!this.unaffiliatedIsInvalid() || this.preExistingUnaffiliatedU13()) {
      organizationOptions.push(unaffiliatedOrgOption);
    }
    return organizationOptions;
  };

  getSelectedSchoolName(): string {
    const { selectedOrg } = this.state;
    return selectedOrg ? selectedOrg.label : 'Not affiliated with a school';
  }

  getSelectedSchoolId(): string {
    const { selectedOrg } = this.state;
    return selectedOrg ? selectedOrg.value : UNAFFILIATED_CLASS_ID;
  }

  getOrganizationForSubmit(): ?OrgOption {
    return this.state.selectedOrg;
  }

  shouldShowSchool(): boolean {
    if (CurrentUser.isSignedIn()) {
      return CurrentUser.getUser().isOrganizationPickerEligible();
    }
    return false;
  }

  preExistingUnaffiliatedU13(): boolean {
    return this.state && !this.state.selectedOrg && this.unaffiliatedIsInvalid();
  }

  renderMenu() {
    // TODO: rm un-namspaced classes

    const menu = (
      <Menu
        className={cx('GroupSchoolSelect GroupSchoolSelect--menu', {
          'GroupSchoolSelect--unaffiliated': !this.unaffiliatedIsInvalid(),
          'GroupForm-schoolMenu': true,
          'GroupForm-hasUnaffiliated': !this.unaffiliatedIsInvalid(),
        })}
        selectedKey={this.getSelectedSchoolId().toString()}>
        {this.getSchoolSelectOptions().map(option => (
          <MenuItem key={option.value} onSelect={this.handleSchoolChange}>
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    );

    return (
      <div>
        {this.state.selectedOrg && this.props.prefix}
        <LayerTrigger layer={menu} trigger="click" placement="bottom" alignment="middle">
          <Link href="#">
            <span>
              {this.getSelectedSchoolName()}
              <Glyph name="chevron-down" size="xsmall" />
            </span>
          </Link>
        </LayerTrigger>
      </div>
    );
  }

  renderSelect() {
    return (
      <FormElement
        label="School"
        ref="school"
        id="schoolSelect"
        onValidChange={value => this.handleSchoolChange(value)}
        validate={value => {
          const invalid = this.unaffiliatedIsInvalid() && value === '-1';
          if (invalid) {
            throw Error(
              'The class must be connected to a school if it contains students under 13.'
            );
          }
        }}
        element={Select}
        elementProps={{
          name: 'school',
          searchable: false,
          options: this.getSchoolSelectOptions(),
          value: this.getSelectedSchoolId(),
          className: cx('GroupSchoolSelect GroupSchoolSelect--select', {
            'GroupSchoolSelect--unaffiliated': !this.unaffiliatedIsInvalid(),
            'GroupForm-schoolSelect': true,
            'GroupForm-hasUnaffiliated': !this.unaffiliatedIsInvalid(),
          }),
        }}
      />
    );
  }

  render() {
    if (!this.shouldShowSchool()) {
      return null;
    }

    return this.props.input === 'menu' ? this.renderMenu() : this.renderSelect();
  }
}

export { GroupSchoolSelect };

const withOrganizations = graphql(GROUP_SCHOOL_SELECT_QUERY);

export default withOrganizations(GroupSchoolSelect);
